'use client';

import * as RadixSlider from '@radix-ui/react-slider';
import { ComponentPropsWithoutRef } from 'react';
import { tv } from 'tailwind-variants';

const sliderStyles = tv({
  slots: {
    root: [
      'relative',
      'flex',
      'w-full',
      'touch-none',
      'select-none',
      'items-center',
      'data-[orientation=vertical]:h-full',
      'data-[orientation=vertical]:w-auto',
      'data-[orientation=vertical]:flex-col',
      'data-[disabled]:cursor-not-allowed',
      'data-[disabled]:opacity-50',
    ],
    track: [
      'relative',
      'grow',
      'overflow-hidden',
      'rounded-full',
      'bg-token-gray-400',
      'data-[orientation=horizontal]:h-1.5',
      'data-[orientation=horizontal]:w-full',
      'data-[orientation=vertical]:h-full',
      'data-[orientation=vertical]:w-1.5',
    ],
    range: [
      'absolute',
      'bg-special-background-button-purple',
      'data-[orientation=horizontal]:h-full',
      'data-[orientation=vertical]:w-full',
    ],
    thumb: [
      'block',
      'size-4',
      'rounded-full',
      'bg-special-content-button-purple',
      'shadow-15',
      'ring-2',
      'ring-special-background-button-purple',
      'cursor-grab',
      'transition-transform',
      'duration-150',
      'ease-out',
      'hover:scale-110',
      'active:cursor-grabbing',
      'focus-visible:outline-none',
      'focus-visible:ring-4',
      'focus-visible:ring-token-purple-400',
      'data-[disabled]:pointer-events-none',
      'data-[disabled]:cursor-not-allowed',
    ],
  },
});

type RadixSliderProps = ComponentPropsWithoutRef<typeof RadixSlider.Root>;

type SliderProps = RadixSliderProps & {
  /**
   * Accessible label applied to the slider thumb(s). Pass a single string to
   * apply the same label to every thumb, or an array to label each thumb
   * positionally (useful for range sliders).
   */
  thumbAriaLabel?: string | string[];
};

/**
 * Reusable slider primitive built on top of `@radix-ui/react-slider`.
 *
 * The component is fully controlled by Radix's `value`/`defaultValue` +
 * `onValueChange` pair (both are arrays of numbers to support range sliders).
 * Pass `[n]` for a single-thumb slider and `[a, b]` for a range slider — the
 * matching number of thumbs is rendered automatically.
 */
export function Slider({ value, defaultValue, className, thumbAriaLabel, ...props }: SliderProps) {
  const { root, track, range, thumb } = sliderStyles();
  const thumbs = value ?? defaultValue ?? [0];

  return (
    <RadixSlider.Root
      className={root({ class: className })}
      value={value}
      defaultValue={defaultValue}
      {...props}
    >
      <RadixSlider.Track className={track()}>
        <RadixSlider.Range className={range()} />
      </RadixSlider.Track>
      {thumbs.map((_, index) => (
        <RadixSlider.Thumb
          key={index}
          className={thumb()}
          aria-label={Array.isArray(thumbAriaLabel) ? thumbAriaLabel[index] : thumbAriaLabel}
        />
      ))}
    </RadixSlider.Root>
  );
}
